<template>
  <div class="flex-1 p-6 flex w-full">
    <div v-if="current_metric.metric_class == 'computed_metric'" class="h-full w-full overflow-auto">
      <Form v-slot="{}"
            :initial-values="initial_values"
            :validation-schema="schema"
            class="space-y-5 h-full"
            @submit="onSubmit">
        <div class="flex items-start">
          <div class="bg-white w-64 h-[510px] p-2 mr-2 rounded-md relative">
            <div class="text-xl mb-10">
              {{ $t("Creation Steps") }}
            </div>

            <div v-for="(element, index) in tabs_create_metric" :key="element" class="cursor-pointer">
              <div class="flex items-center space-x-2" @click="selected_tab_index = index">
                <div class="w-8 h-8 bg-[#E4E7EC] rounded-full flex items-center justify-center">
                  <div
                    v-if="selected_tab_index == index || stepCompletedCalc(index)"
                    :class="[
                      { '!bg-purple-500': selected_tab_index == index && !stepCompletedCalc(index) },
                      { '!bg-green-500 w-8 h-8': stepCompletedCalc(index) }
                    ]"
                    class="w-6 h-6 bg-transparent rounded-full" />

                  <Check v-if="stepCompletedCalc(index) && selected_tab_index !== index" class="w-5 h-5 text-white absolute" />
                  <div v-if="!stepCompletedCalc(index) || selected_tab_index == index" class="w-2 absolute h-2 bg-white rounded-full" />
                </div>
                <div class="text-sm">
                  {{ element }}
                </div>
              </div>
              <div
                v-if="index < tabs_create_metric.length - 1"
                :class="[{ '!bg-green-500': stepCompletedCalc(index) }]"
                class="h-16 ml-3.5 w-[2px] bg-[#E4E7EC]" />
            </div>
          </div>
          <TabGroup
            vertical
            :selected-index="selected_tab_index"
            class="relative !overflow-auto transition h-full w-full ease-in-out rounded-lg focus:outline-none bg-light-box-background p-4 dark:bg-dark-box-background duration-side-menu-transition-duration"
            as="div"
            @change="selected_tab_index = $event">
            <TabPanels class="h-full !overflow-auto">
              <TabPanel v-for="tab in tabs_create_metric"
                        v-slot="{ selected }"
                        :key="tab"
                        static
                        class="outline-none h-full overflow-auto">
                <div v-if="selected" class="text-2xl font-medium">
                  {{ tabs_create_metric[selected_tab_index] }}
                </div>

                <keep-alive>
                  <Details
                    v-if="selected && selected_tab_index == 0"
                    :metric="current_metric"
                    class="p-2 mt-2"
                    @update="
                      ($event) => {
                        current_metric.name = $event.name
                        current_metric.object_type = $event.object_type
                        current_metric.metric_tag = $event.metric_tag
                        current_metric.description = $event.description
                      }
                    " />

                  <Display
                    v-else-if="selected && selected_tab_index == 1"
                    :metric="current_metric"
                    class="p-2 mt-2"
                    @update="
                      ($event) => {
                        current_metric.is_public = $event.is_public
                        current_metric.is_rs_readonly = $event.is_rs_readonly
                        current_metric.input_type = $event.input_type
                        current_metric.input_options = $event.input_options
                        current_metric.aggregation_method = $event.aggregation_method
                      }
                    " />

                  <Formula
                    v-else-if="selected && selected_tab_index == 2"
                    :metric="current_metric"
                    class="h-full !overflow-y-auto"
                    :show-dropdown-active="show_dropdown_active"
                    @update="
                      ($event) => {
                        current_metric.formulary = $event.formulary
                        current_metric.cases = $event.cases
                      }
                    " />
                  <Colors
                    v-else-if="selected && selected_tab_index == 3"
                    :metric="current_metric"
                    class="p-2 h-[400px] overflow-y-auto mt-2"
                    @update="
                      ($event) => {
                        handleColorsChange($event)
                      }
                    " />
                </keep-alive>
              </TabPanel>

              <div class="flex w-full">
                <div v-if="selected_tab_index" class="btn text-dark-brand-blue cursor-pointer" @click.stop="selected_tab_index -= 1">
                  {{ $t("Previous Step") }}
                </div>
                <div class="btn ml-auto cancel-btn mr-2 -mt-4 h-12" @click="$emit('cancel')">
                  {{ $t("Cancel") }}
                </div>
                <div
                  :class="{ 'pointer-events-none opacity-30': !all_steps_completed && selected_tab_index == 3 }"
                  class="btn primary-btn w-fit py-3 relative bottom-4"
                  @click.stop="handleNextClick">
                  <ArrowDownToDot
                    v-if="selected_tab_index !== 3"
                    class="w-5 h-5 mr-4 text-white"
                    :class="[$store.state.portfolio.ui_options?.language == 'ar' ? 'rotate-90' : '-rotate-90']" />
                  <GitBranchPlus v-else class="w-5 h-5 mr-4" color="white" />
                  {{ selected_tab_index == 3 ? (current_metric.id ? $t("Update Metric") : $t("Create Metric")) : $t("Next Step") }}
                </div>
              </div>
            </TabPanels>
          </TabGroup>
        </div>
      </Form>

      <Modal v-model:is-open="show_errors" default-success-msg="">
        <template #form="{ close: closeModal }">
          <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
            {{ $t("Please Fix the following errors") }}
          </h3>
          <div v-if="server_errors.length > 1" class="mt-2">
            <p v-for="(er, index) in server_errors" :key="er" class="text-sm text-gray-500">
              <strong>{{ index + 1 }}. </strong> {{ er }}
            </p>
          </div>
          <div v-else class="mt-2">
            {{ server_errors[0] }}
          </div>

          <div class="flex items-center justify-center gap-2 mt-5 text-center">
            <button type="button" class="w-40 primary-btn" @click="closeModal">{{ $t("OK") }}</button>
          </div>
        </template>
      </Modal>
    </div>
    <EditCustomMetricForm v-else-if="current_metric?.id" :metric="current_metric" @cancel="$emit('cancel')" @done="$emit('done')" />
    <NewMetricForm v-else :object-type="current_metric.object_type" @cancel="$emit('cancel')" @done="$emit('done')" />

    <!-- Metric Preview -->
    <div v-if="current_metric?.metric_class != 'custom_metric'" class="w-96 p-6 border-l border-gray-200 dark:border-dark-divider-border">
      <h3 class="text-lg font-medium mb-4">
        {{ $t("Preview") }}
      </h3>
      <div v-if="!current_metric?.aggregation_method">
        <p class="text-sm text-gray-500 mb-4">
          {{ $t("A KPI is used to show results for ONE record at a time.") }}
        </p>
        <div class="bg-white dark:bg-dark-box-background border dark:border-dark-divider-border rounded-md p-4">
          <div class="flex justify-between mb-2">
            <span class="text-sm font-medium">{{ $t("Name") }}</span>
            <span class="text-sm">{{ current_metric?.name || $t("Metric Name") }}</span>
          </div>
          <div class="space-y-2">
            <div v-for="name in ['William', 'David', 'Steven', 'Peter']" :key="name" class="flex justify-between items-center">
              <span class="text-sm text-gray-500">{{ name }}</span>
              <div class="flex items-center">
                <div class="w-2 h-2 rounded-full bg-gray-300 mr-2" />
                <span class="text-sm">{{ $t("Value") }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <p class="text-sm text-gray-500 mb-4">
          {{ $t("A Group KPI is used to show results for MANY records or a GROUP of records at a time.") }}
        </p>
        <RollUpMetric :metric="preview_metric" :item-layout-width="12" :item-layout-height="6" />
      </div>
    </div>
  </div>
</template>

<script>
import NewMetricForm from "@/components/forms/NewMetricForm.vue"
import EditCustomMetricForm from "@/components/forms/EditCustomMetricForm.vue"
import RollUpMetric from "@/components/shared/rollup-metrics/RollUpMetric.vue"
import * as Yup from "yup"
import { Formulary } from "@/models/metric"
import { Form } from "vee-validate"
import { TabGroup, TabPanel, TabPanels } from "@headlessui/vue"
import Modal from "@/components/shared/Modal.vue"
import Details from "./form-tabs/Details.vue"
import Formula from "./form-tabs/Formula.vue"
import Display from "./form-tabs/Display.vue"
import Colors from "./form-tabs/Colors.vue"
import { utils } from "@/store/helpers"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { GitBranchPlus, Check, ArrowDownToDot } from "lucide-vue-next"

export default {
  components: {
    NewMetricForm,
    EditCustomMetricForm,
    RollUpMetric,
    ArrowDownToDot,
    GitBranchPlus,
    Check,
    Form,
    TabGroup,
    TabPanel,
    TabPanels,
    Details,
    Formula,
    Display,
    Colors,
    Modal
  },
  props: {
    metric: {
      type: Object,
      required: true
    }
  },
  emits: ["hide-form", "hide", "cancel", "close", "updated", "update", "created", "done"],
  data() {
    return {
      selected_tab_index: 0,
      tabs_create_metric: [this.$t("Details"), this.$t("Display"), this.$t("Formula"), this.$t("Coloring Options")],
      current_metric: null,
      server_errors: [],
      show_errors: false,
      schema: false,
      initial_values: {},
      redirect_url: null,
      page_reference: null,
      query_queue: "",
      query_timeout: null,
      show_dropdown_active: false
    }
  },

  computed: {
    preview_metric() {
      const metric_calc = {
        ...this.$store.state.metrics.filter((m) => m.data_type === "Number")[0],
        name: this.current_metric?.name ? this.current_metric?.name : "Metric Name",
        display: this.current_metric?.name ? this.current_metric?.name : "Metric Name",
        label: this.current_metric?.name ? this.current_metric?.name : "Metric Name",
        value: 100000,
        history: [
          {
            datetime: `${new Date().getFullYear()}-01-01T00:00:00Z`,
            value: 80000
          },
          {
            datetime: `${new Date().getFullYear()}-02-01T00:00:00Z`,
            value: 120000
          },
          {
            datetime: `${new Date().getFullYear()}-03-01T00:00:00Z`,
            value: 100000
          }
        ]
      }
      return metric_calc
    },
    all_steps_completed() {
      if (!this.current_metric.name) {
        return false
      }

      if (!this.current_metric.formulary?.expression && !this.current_metric?.formulary?.cases) {
        return false
      }

      return true
    }
  },

  watch: {
    current_metric: {
      deep: true,
      handler() {
        if (this.selected_tab_index == 3) {
          this.$emit("update", { ...this.$utils.clone(this.current_metric), color_tab_active: true })
        } else {
          this.$emit("update", this.current_metric)
        }
      }
    },

    selected_tab_index() {
      if (this.selected_tab_index == 3) {
        this.$emit("update", { ...this.$utils.clone(this.current_metric), color_tab_active: true })
      } else {
        this.$emit("update", { ...this.current_metric, color_tab_active: false })
      }
    }
  },

  created() {
    this.register_listeners()

    this.current_metric = utils.clone(this.metric)

    if (this.current_metric?.id) {
      this.current_metric["display_step_complete"] = true
    }
    if (this.$route.query?.redirect_url) {
      this.redirect_url = this.$route.query?.redirect_url
      this.page_reference = this.$route.params?.page_ref
      this.current_metric["aggregation_method"] = "sum"
      if (
        this.$store.state.object_types.find(
          (o) =>
            (o.plural_label ? o.plural_label : this.$utils.capitalize(this.$utils.makePlural(o.type))) ==
            this.redirect_url.split("/").slice(-1)
        )?.type
      ) {
        this.current_metric["object_type"] = this.$store.state.object_types.find(
          (o) =>
            (o.plural_label ? o.plural_label : this.$utils.capitalize(this.$utils.makePlural(o.type))) ==
            this.redirect_url.split("/").slice(-1)
        )?.type
      }
    }

    this.initial_values = { ...this.current_metric }
    if (this.current_metric.metric_class == "custom_metric") {
      this.schema = Yup.object().shape({
        name: Yup.string().required(this.$t("Metric name is required"))
      })
    } else {
      this.schema = Yup.object().shape({
        name: Yup.string().required(this.$t("Metric name is required")),
        formulary: Yup.object()
          .shape({})
          .required(this.$t("Formula is required"))
          .test("valid_formula", this.$t("Formula is not valid"), (value) => value && new Formulary(value).isValid())
      })
    }
  },
  mounted() {
    this.$store.dispatch("ui/setCurrentAdminBreadcrumb", ["Metric"])
  },
  activated() {
    this.$store.dispatch("ui/setCurrentAdminBreadcrumb", ["Metric"])
  },
  methods: {
    handleColorsChange(e) {
      this.current_metric.metric_colors = e.metric_colors
    },
    handleNextClick() {
      if (this.selected_tab_index == 3) {
        this.onSubmit()
      } else {
        this.selected_tab_index += 1

        if (this.selected_tab_index == 2) {
          this.current_metric.display_step_complete = true
        }
      }
    },

    stepCompletedCalc(index) {
      const tab = this.tabs_create_metric[index]
      if (tab == "Details") {
        if (!this.current_metric.name) {
          return false
        }
        return true
      }

      if (tab == "Formula") {
        if (!this.current_metric.formulary?.expression && !this.current_metric.formulary?.cases) {
          return false
        }
        return true
      }

      if (tab == "Display") {
        if (this.current_metric.display_step_complete) {
          return true
        }
      }

      if (tab == "Coloring Options") {
        if (this.current_metric.metric_colors?.ranges?.length) {
          return true
        }
      }
      return false
    },
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.TRIGGER_FORMULA_CLICK, (e) => {
        if (this.selected_tab_index == 2 && e.code !== "Enter") {
          this.handleKeyPress(e)
        }
      })

      this.$emitter.on(ALL_EMITTERS.SHOW_DROPDOWN_ACTIVE, (value) => {
        this.show_dropdown_active = value
      })
    },
    onSubmit() {
      if (!this.current_metric?.id) {
        this.addMetric()
      } else {
        this.updateMetric()
      }
    },

    addMetric() {
      if (this.current_metric?.formulary?.expression) {
        this.current_metric.formulary.expression = this.parseForServer(this.current_metric?.formulary?.expression)
      }

      if (this.current_metric.name.toLowerCase().includes("renewal date")) {
        this.server_errors = []
        this.server_errors.push(this.$t("Restricted name"))
        this.show_errors = true
      } else {
        this.current_metric['organization'] = this.$store.state.organization.id
        this.$store
          .dispatch("data/addMetric", this.current_metric)
          .then((data) => {
            this.$store.dispatch("data/getMetrics")

            this.$toast.open({
              message: this.$t("Metric") + ` ${data.name} ` + this.$t("created"),
              position: "top",
              type: "success",
              duration: 3000
            })

            if (this.redirect_url) {
              this.$emitter.emit(ALL_EMITTERS.METRIC_CREATED_FOR_MODAL, {
                metric: data,
                page_reference: this.page_reference
              })
              this.$router.push(`${this.redirect_url}`)
            } else {
              this.$emit("created", data)
            }
          })
          .catch((er) => {
            let error = er.response.data
            this.server_errors = []
            if (error["code"] == "BAD_REQUEST") {
              if (Object.keys(error["errors"]).length > 0) {
                Object.keys(error["errors"]).forEach((key) => {
                  if (key == "name") {
                    this.server_errors.push(this.$t(this.$t("Duplicate metric name")))
                  } else {
                    this.server_errors.push(...error["errors"][key])
                  }
                })
              }
            } else if (error["code"] == "FORBIDDEN") {
              this.server_errors.push(error.errors.detail)
            }
            this.show_errors = true
          })
      }
    },

    handleKeyPress(e) {
      if (e.code == "Backspace" && !this.show_dropdown_active) {
        if (this.current_metric.formulary.expression?.length || this.current_metric.formulary.cases?.length) {
          this.$emitter.emit(ALL_EMITTERS.BACKSPACECLICKFORMULA)
        }
      } else if (!this.show_dropdown_active) {
        if (e.key !== "Shift" && e.code !== "Backspace") {
          this.query_queue += e.key

          clearTimeout(this.query_timeout)

          this.query_timeout = setTimeout(() => {
            this.$emitter.emit(ALL_EMITTERS.KEYCLICKFORMULA, this.query_queue)
            this.query_queue = ""
          }, 400)
        }
      }
    },

    parseForServer(exp) {
      let list = []

      if (Array.isArray(exp)) {
        exp.forEach((v, index) => {
          if (exp[index]?.type == "input") {
            if (exp[index]?.data_type == "Boolean") {
              list.push(v.value ? "True" : "False")
            } else if (exp[index].data_type == "List") {
              list.push("[")
              if (v.value) {
                list.push(v.value.map((x) => `'${x}'`).join(","))
              }
              list.push("]")
            } else if (utils.isNumber(exp[index].value)) {
              list.push(String(v.value).replace(",", ""))
            } else {
              if (v?.display == "In Date Range") {
                list.push("(")
                list.push(`'${v.value.start?.toISOString()}'`)
                list.push(",")
                list.push(`'${v.value.end?.toISOString()}'`)
                list.push(")")
              } else if (v?.display == "In Range") {
                list.push("(")
                list.push(String(v.value.start).replace(",", ""))
                list.push(",")
                list.push(String(v.value.end).replace(",", ""))
                list.push(")")
              } else if (
                exp[index - 1]?.type == "operator" &&
                (exp[index - 2]?.value == "metric" || exp[index - 2]?.value == "forecast_metrics") &&
                (exp[index - 2]?.data_type == "Date" || exp[index - 2]?.data_type == "DateTime") &&
                exp[index - 1]?.value == "=="
              ) {
                list.push(`'${v.value.toISOString()}'`)
              } else if (
                exp[index - 1]?.type == "operator" &&
                (exp[index - 2]?.value == "metric" || exp[index - 2]?.value == "forecast_metrics") &&
                exp[index - 2]?.data_type == "Boolean"
              ) {
                list.push(v.value ? "True" : "False")
              } else if (
                exp[index - 1]?.type == "operator" &&
                exp[index - 1]?.value == "in" &&
                (exp[index - 2]?.value == "metric" || exp[index - 2]?.value == "forecast_metrics") &&
                exp[index - 2]?.data_type == "Number"
              ) {
                list.push("[")
                if (v.value) {
                  list.push(v.value.join(","))
                }
                list.push("]")
              } else if (
                exp[index - 1]?.type == "operator" &&
                exp[index - 1]?.value == "in" &&
                (exp[index - 2]?.value == "metric" || exp[index - 2]?.value == "forecast_metrics") &&
                exp[index - 2]?.data_type == "String"
              ) {
                list.push("[")
                if (v.value) {
                  list.push(
                    v.value
                      .split(",")
                      .map((x) => `'${x}'`)
                      .join(",")
                  )
                }
                list.push("]")
              } else {
                list.push(`'${v.value}'`)
              }
            }
          } else {
            if (v) {
              list.push(v?.value)
            }
          }
        })
        return list.join(" ")
      } else {
        return exp
      }
    },

    updateMetric() {
      if (this.current_metric?.formulary?.expression) {
        this.current_metric.formulary.expression = this.parseForServer(this.current_metric?.formulary?.expression)
      }

      this.$store
        .dispatch("data/updateMetric", this.current_metric)
        .then((data) => {
          this.$store.dispatch("data/getMetrics")

          this.$toast.open({
            message: this.$t("Metric") + ` ${data.name} ` + this.$t("updated"),
            position: "top",
            type: "success",
            duration: 3000
          })

          this.$emit("updated", data)
          this.$emit("done", data)
        })
        .catch((er) => {
          let error = er.response.data
          this.server_errors = []
          if (error["code"] == "BAD_REQUEST") {
            if (Object.keys(error["errors"]).length > 0) {
              Object.keys(error["errors"]).forEach((key) => {
                if (key == "name") {
                  this.server_errors.push(this.$t("Duplicate metric name"))
                } else {
                  this.server_errors.push(...error["errors"][key])
                }
              })
            }
          } else if (error["code"] == "FORBIDDEN") {
            this.server_errors.push(error.errors.detail)
          }
          this.show_errors = true
        })
    }
  }
}
</script>
